import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArticleCard } from "@/components/kb/ArticleCard";
import { CategoryIcon } from "@/components/kb/CategoryIcon";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";

const DIFFICULTY_LEVELS = ["Principiante", "Intermedio", "Avanzado"];

export default function KBCategory() {
  const { categorySlug } = useParams();
  const [articles, setArticles] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filterDifficulty, setFilterDifficulty] = useState(null);

  const category = categories.find((c) => c.slug === categorySlug);

  useEffect(() => {
    api.get("/kb/categories").then(({ data }) => setCategories(data));
  }, []);

  useEffect(() => {
    if (!category) return;
    const params = { category: category.id };
    if (filterDifficulty) params.difficulty = filterDifficulty;
    api.get("/kb/articles", { params }).then(({ data }) => setArticles(data));
  }, [category, filterDifficulty]);

  if (!category || !articles) {
    return (
      <div className="space-y-6 max-w-5xl mx-auto">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto animate-in-up">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2">
        <Link to="/">
          <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground hover:text-foreground">
            <ChevronLeft className="h-4 w-4" /> Knowledge Hub
          </Button>
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-accent/10 flex items-center justify-center">
          <CategoryIcon slug={category.slug} className="h-6 w-6 text-accent" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">{category.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <Badge
          variant={!filterDifficulty ? "default" : "outline"}
          className="cursor-pointer text-xs"
          onClick={() => setFilterDifficulty(null)}
        >
          Todos ({articles.length})
        </Badge>
        {DIFFICULTY_LEVELS.map((level) => (
          <Badge
            key={level}
            variant={filterDifficulty === level ? "default" : "outline"}
            className="cursor-pointer text-xs"
            onClick={() => setFilterDifficulty(filterDifficulty === level ? null : level)}
          >
            {level}
          </Badge>
        ))}
      </div>

      {/* Articles Grid */}
      {articles.length === 0 ? (
        <p className="text-sm text-muted-foreground py-12 text-center">
          No hay artículos en esta categoría aún.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.map((art) => (
            <ArticleCard key={art.id} article={art} />
          ))}
        </div>
      )}
    </div>
  );
}
